import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft, ArrowUpRight, CheckCircle2,
  Calendar, ArrowLeft,
} from "lucide-react";
import { fetchNewsById, fetchNewsList } from "../../utils/api/newsApi.js";
import { getImageUrl } from "../../utils/imageUtils.js";
import { useLanguage } from "../../context/LanguageContext.jsx";

/* ─── helpers ────── */
const SECTOR_COLORS = {
  'शहरी योजना तथा भौतिक पूर्वाधार': { color: '#d97706', bg: '#fffbeb' },
  'सुरक्षा':                          { color: '#dc2626', bg: '#fef2f2' },
  'खानेपानी':                         { color: '#0891b2', bg: '#ecfeff' },
  'फोहोर तथा प्रदूषण व्यवस्थापन':    { color: '#65a30d', bg: '#f7fee7' },
  'वन, वातावरण र पर्यटन':             { color: '#16a34a', bg: '#f0fdf4' },
  'शिक्षा, अनुसन्धान र ज्ञान':        { color: '#7c3aed', bg: '#f5f3ff' },
  'शिल्प, सीप तथा उद्यमशीलता':       { color: '#b45309', bg: '#fef3c7' },
  'स्वास्थ्य तथा जनस्वास्थ्य':        { color: '#dc2626', bg: '#fef2f2' },
  'स्वस्थ जीवनशैली र उपभोक्ता हित':  { color: '#0d9488', bg: '#f0fdfa' },
  'खेलकुद तथा खेल प्रशिक्षण':        { color: '#ea580c', bg: '#fff7ed' },
  'सूचना प्रविधि तथा स्मार्ट सिटी':  { color: '#0ea5e9', bg: '#f0f9ff' },
  'मिडिया तथा सञ्चार':                { color: '#6366f1', bg: '#eef2ff' },
  'व्यापार, उद्योग र आर्थिक विकास':  { color: '#059669', bg: '#ecfdf5' },
  'कृषि तथा सहकारी':                  { color: '#4d7c0f', bg: '#f7fee7' },
  'विपद् व्यवस्थापन':                 { color: '#9f1239', bg: '#fff1f2' },
  'कला, संस्कृति र पहिचान':           { color: '#db2777', bg: '#fdf2f8' },
  'सुशासन, आर्थिक नीति र वित्तीय व्यवस्थापन': { color: '#1e3a8a', bg: '#eff6ff' },
  'सामाजिक सुरक्षा र समावेशी विकास': { color: '#7c3aed', bg: '#f5f3ff' },
  'महिला, बालबालिका र युवा विकास':    { color: '#be185d', bg: '#fdf2f8' },
  'अन्तर्राष्ट्रिय सम्बन्ध':          { color: '#0369a1', bg: '#f0f9ff' },
};

const getSectorMeta = (sector) =>
  SECTOR_COLORS[sector] || { color: '#166534', bg: '#f0fdf4' };

const formatDate = (d, lang) =>
  new Date(d).toLocaleDateString(lang === 'ne' ? 'ne-NP' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const CONTENT = {
  ne: {
    backToNewsList: "समाचार सूचीमा फर्कनुहोस्",
    backToNews: "समाचारमा फर्कनुहोस्",
    loading: "समाचार लोड हुँदैछ…",
    notFoundTitle: "समाचार फेला परेन",
    notFoundSubtitle: "यो समाचार हटाइएको हुनसक्छ वा लिङ्क गलत छ।",
    newsTeam: "समाचार टोली",
    relatedNews: "सम्बन्धित समाचार",
    noRelated: "अहिले सम्बन्धित समाचार उपलब्ध छैन",
  },
  en: {
    backToNewsList: "Back to News List",
    backToNews: "Back to News",
    loading: "Loading news…",
    notFoundTitle: "News not found",
    notFoundSubtitle: "This article may have been removed, or the link is incorrect.",
    newsTeam: "News Team",
    relatedNews: "Related News",
    noRelated: "No related news available right now",
  },
};

/* ─── toast hook ── */
function useToast() {
  const [msg, setMsg] = useState("");
  const show = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2200); };
  return { msg, show };
}

/* ─── Loading / Not found ── */
function PageState({ icon, title, subtitle, showBack, backLabel }) {
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <p className="text-5xl mb-3">{icon}</p>
        <h1 className="font-bold text-xl text-gray-900 mb-2">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mb-6">{subtitle}</p>}
        {showBack && (
          <Link
            to="/news"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white no-underline"
            style={{ background: '#166534' }}
          >
            <ArrowLeft className="w-4 h-4" /> {backLabel}
          </Link>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════ */
export function NewsDetailPage() {
  const { id } = useParams();
  const { language } = useLanguage();
  const t = CONTENT[language];

  const [article, setArticle] = useState(null);
  const [allNews, setAllNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const { msg: toast } = useToast();

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const data = await fetchNewsById(id);
        if (active) setArticle(data);
      } catch (err) {
        console.error("Error fetching article:", err);
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    fetchNewsList()
      .then((res) => setAllNews(res?.data ?? []))
      .catch((err) => console.error("Error fetching news list:", err));
  }, []);

  const related = useMemo(() => {
    if (!article) return [];
    const sameSector = allNews
      .filter((a) => a._id !== article._id && a.sector === article.sector);

    if (sameSector.length > 0) return sameSector.slice(0, 4);

    return allNews
      .filter((a) => a._id !== article._id)
      .slice(0, 4);
  }, [allNews, article]);

  const { prevArticle, nextArticle } = useMemo(() => {
    if (!article) return { prevArticle: null, nextArticle: null };
    const sorted = [...allNews].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const idx = sorted.findIndex((a) => a._id === article._id);
    if (idx === -1) return { prevArticle: null, nextArticle: null };
    return { prevArticle: sorted[idx + 1] || null, nextArticle: sorted[idx - 1] || null };
  }, [allNews, article]);

  if (loading) return <PageState icon="📰" title={t.loading} />;

  if (notFound || !article) {
    return (
      <PageState
        icon="📰"
        title={t.notFoundTitle}
        subtitle={t.notFoundSubtitle}
        showBack
        backLabel={t.backToNewsList}
      />
    );
  }

  const m = getSectorMeta(article.sector);
  const paragraphs = (article.content || '').split('\n').filter((p) => p.trim());

  const rawAuthor = article.author;
  const authorName =
    rawAuthor && typeof rawAuthor === 'object' && rawAuthor.name
      ? rawAuthor.name
      : typeof rawAuthor === 'string' && rawAuthor.length < 60
        ? rawAuthor
        : t.newsTeam;

  return (
    <div className="min-h-screen bg-slate-50 pt-16 font-sans">

      {toast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl shadow-xl flex items-center gap-2 bg-gray-900 text-white text-xs">
          <CheckCircle2 className="w-4 h-4 text-green-400" />
          {toast}
        </div>
      )}

      {/* ── Hero ── */}
      <div className="relative w-full overflow-hidden"style={{ height: "clamp(320px,52vw,580px)" }}>
        {article.imageUrl ? (
          <img src={getImageUrl(article.imageUrl)} alt={article.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-green-900 flex items-center justify-center">
            <span className="text-white/30 text-6xl">📰</span>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 to-black/80" />

        <Link
          to="/news"
          className="absolute top-10 left-10 z-20 flex items-center gap-1.5  text-white/90 hover:text-white transition-colors no-underline text-xs font-medium bg-amber-700/90 backdrop-blur-sm px-3.5 py-2 rounded-full"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {t.backToNews}
        </Link>

        {/* Hero content */}
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-full max-w-5xl px-6 pb-10">
          <h1
            className="text-white font-bold leading-tight mb-5"
            style={{ fontSize: "clamp(1.5rem,3.2vw,2.6rem)", maxWidth: "780px" }}
          >
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2.5">
              <div
                className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm text-white border-2 border-white/30"
                style={{ background: m.color }}
              >
                {authorName.charAt(0)}
              </div>
              <p className="text-white font-semibold text-sm leading-none">{authorName}</p>
            </div>

            <span className="flex items-center gap-1.5 text-white/50 text-xs">
              <Calendar className="w-3.5 h-3.5" />{formatDate(article.createdAt, language)}
            </span>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col lg:flex-row gap-12">

          {/* ── Article ── */}
          <article className="flex-1 min-w-0">

            {/* Lead excerpt */}
            <p className="text-gray-900 text-[1.1rem] leading-[1.85] font-medium mb-6">
              {article.excerpt}
            </p>

            {/* Content paragraphs */}
            <div className="space-y-5 mb-8">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-gray-700 text-[0.95rem] leading-[1.9]">
                  {p}
                </p>
              ))}
            </div>


          </article>

          {/* ── Sidebar ── */}
          <aside className="w-full lg:w-72 shrink-0">
            <div className="lg:sticky lg:top-24">
              <div className="rounded-2xl border border-slate-200 p-5 bg-white">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-green-50">
                    <ArrowUpRight className="w-3.5 h-3.5 text-green-800" />
                  </div>
                  <p className="font-semibold text-sm text-gray-900">{t.relatedNews}</p>
                </div>
                <div className="space-y-4">
                  {related.length > 0 ? (
                    related.map((a) => (
                      <SidebarRelatedItem key={a._id} article={a} />
                    ))
                  ) : (
                    <p className="text-xs text-gray-400 text-center py-4">
                      {t.noRelated}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}



/* ── Sidebar Related Item ── */
function SidebarRelatedItem({ article }) {
  const [hov, setHov] = useState(false);
  const m = getSectorMeta(article.sector);
  return (
    <Link
      to={`/news/${article._id}`}
      className="flex gap-3 cursor-pointer group no-underline"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      <div className="w-16 h-14 rounded-lg overflow-hidden shrink-0 bg-gray-100">
        {article.imageUrl ? (
          <img
            src={getImageUrl(article.imageUrl)}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-300"
            style={{ transform: hov ? "scale(1.08)" : "scale(1)" }}
          />
        ) : (
          <div className="w-full h-full bg-green-900" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p
          className="line-clamp-2 text-xs font-semibold leading-snug transition-colors"
          style={{ color: hov ? m.color : "#111827" }}
        >
          {article.title}
        </p>
        <span
          className="text-[0.6rem] font-semibold px-1.5 py-0.5 rounded mt-1 inline-block"
          style={{ background: m.bg, color: m.color }}
        >
          {article.sector}
        </span>
      </div>
    </Link>
  );
}

export default NewsDetailPage;